import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { readCartResponse, collectCartSectionIds } from '@theme/r-cart-actions';
import { showToast } from '@theme/r-toast';
import { cartValue, track } from '@theme/r-analytics';

/**
 * <r-cart-gift-wrap> — "Add entire order to gift box" (snippets/r-cart-gift-wrap.liquid).
 *
 * The cart is the only state. The checkbox is rendered from whether the gift box
 * is a line in the cart and re-rendered with the cart on every update, so this
 * module never remembers "wrapped" on its own; it asks the cart for a state and
 * then lets the section re-render show what the cart actually holds.
 *
 * Reliability rules, each answering a way it went wrong elsewhere:
 *
 * - Absolute, not relative. Every write is `/cart/update.js` with a target
 *   quantity (1 or 0), never an add. A double click, a retried request or a
 *   second tab cannot stack a second gift box, and re-sending the same intent
 *   is harmless.
 * - Verified. The response is checked for exactly the intended quantity. If the
 *   cart disagrees (the box sits on another variant, or on two lines), one
 *   follow-up keyed by line key corrects it.
 * - Latest intent wins. Requests run one at a time; a click made while one is
 *   in flight supersedes any queued request and only the newest one runs. The
 *   checkbox is never disabled, so focus stays on it and a quick
 *   tick-untick-tick settles on ticked with no more than two requests.
 * - Failures are visible and undone. A rejected or unreadable response (an edge
 *   challenge answers 429 HTML, see readCartResponse) puts the checkbox back to
 *   the cart's state and raises a toast, never a silent mismatch.
 * - Checkout waits. A Check out submitted mid-write is held until it settles.
 * - Self-healing. On every cart update from anywhere else, a gift box left with
 *   nothing to wrap is removed, and more than one is brought back to one. The
 *   same check runs when the element first connects, for a cart that arrived
 *   that way from another tab or an older product page.
 *
 * Updates this module dispatches carry `detail.data.source === 'r-cart-gift-wrap'`
 * and are ignored by its own listener, so a correction cannot feed back on itself.
 */

const SOURCE = 'r-cart-gift-wrap';

/**
 * @typedef {{ key: string, product_id: number, quantity: number, requires_shipping?: boolean, product_type?: string, properties?: Record<string, unknown> }} CartLine
 * @typedef {{ items?: CartLine[], item_count?: number, items_subtotal_price?: number, sections?: Record<string, string | null> }} Cart
 * @typedef {{ productId: string, variantId: string, excludedTypes: Set<string>, errorMessage: string }} Config
 * @typedef {{ cart: Cart, sectionIds: Set<string> }} Applied
 */

/** Ticket of the newest intent; a queued run holding an older ticket is skipped. */
let latestTicket = 0;

/** Requests run strictly one after another. @type {Promise<void>} */
let queue = Promise.resolve();

/** Intents taken but not yet settled. While non-zero, reconcile stands down. */
let pending = 0;

/**
 * A successful write whose re-render was held back because a newer intent was
 * queued behind it. Dispatched if that newer intent fails, so the page never
 * shows a cart older than the real one. @type {Applied | null}
 */
let undispatched = null;

/** @returns {HTMLElement[]} */
function hosts() {
  return /** @type {HTMLElement[]} */ (Array.from(document.querySelectorAll('r-cart-gift-wrap')));
}

/** @returns {Config | null} */
function readConfig() {
  const host = hosts()[0];
  const productId = host?.dataset.productId;
  const variantId = host?.dataset.variantId;
  if (!host || !productId || !variantId) return null;
  // Liquid hands over the normalised "|gift set|candles|" list.
  const excludedTypes = new Set(
    (host.dataset.excludedTypes ?? '')
      .split('|')
      .map((type) => type.trim())
      .filter(Boolean)
  );
  return { productId, variantId, excludedTypes, errorMessage: host.dataset.errorMessage || '' };
}

/**
 * @param {Cart | null | undefined} cart
 * @param {string} productId
 * @returns {CartLine[]}
 */
function giftLines(cart, productId) {
  return (cart?.items ?? []).filter((item) => item && String(item.product_id) === productId);
}

/**
 * Lines the gift box would wrap: shipped items other than the box itself, the
 * auto-added free gift (which only exists alongside real items anyway) and
 * pre-boxed product types in `data-excluded-types` (gift sets, candles). The box
 * is checked first because its own type is "Gift Set" too. An e-gift card
 * does not ship, so it has nothing to go in a box either.
 * @param {Cart | null | undefined} cart
 * @param {Config} config
 */
function wrappableCount(cart, config) {
  return (cart?.items ?? []).filter((item) => {
    if (!item) return false;
    const id = String(item.product_id);
    return (
      id !== config.productId &&
      !config.excludedTypes.has(String(item.product_type ?? '').trim().toLowerCase()) &&
      item.properties?._free_gift !== 'true' &&
      item.requires_shipping !== false
    );
  }).length;
}

/** @param {CartLine[]} lines */
function quantityOf(lines) {
  return lines.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);
}

/**
 * @param {Record<string, number>} updates - Variant ids or line keys → target quantity.
 * @returns {Promise<{ ok: boolean, data: any, error: string | null, sectionIds: Set<string> }>}
 */
async function postUpdate(updates) {
  const sectionIds = collectCartSectionIds();
  const body = JSON.stringify({
    updates,
    sections: Array.from(sectionIds).join(','),
    sections_url: window.location.pathname,
  });
  const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
  const read = await readCartResponse(response);
  return { ok: read.ok && Array.isArray(read.data?.items), data: read.data, error: read.error, sectionIds };
}

/**
 * Brings the cart to exactly `want ? 1 : 0` gift boxes.
 * @param {boolean} want
 * @param {Config} config
 * @returns {Promise<Applied>}
 */
async function apply(want, config) {
  const target = want ? 1 : 0;

  let result = await postUpdate({ [config.variantId]: target });
  if (!result.ok) throw new Error(result.error || 'update failed');

  const lines = giftLines(result.data, config.productId);
  const quantity = quantityOf(lines);

  if (quantity !== target) {
    // Nothing to correct by key: the add itself was refused (sold out between
    // render and click). The Cart API answers that with a 200 and no new line.
    if (want && lines.length === 0) throw new Error('gift box was not added');

    /** @type {Record<string, number>} */
    const updates = {};
    lines.forEach((line, index) => {
      updates[line.key] = want && index === 0 ? 1 : 0;
    });
    result = await postUpdate(updates);
    if (!result.ok) throw new Error(result.error || 'correction failed');
    if (quantityOf(giftLines(result.data, config.productId)) !== target) throw new Error('cart did not settle');
  }

  return { cart: result.data, sectionIds: result.sectionIds };
}

/** @param {Applied} applied */
function dispatch(applied) {
  const { cart, sectionIds } = applied;
  const [sourceSectionId] = sectionIds;
  document.dispatchEvent(
    new CartUpdateEvent(cart, sourceSectionId ?? '', {
      itemCount: cart.item_count,
      source: SOURCE,
      sections: cart.sections ?? undefined,
    })
  );
}

/** Puts every checkbox back to what the cart held at the last render. */
function resync() {
  for (const host of hosts()) {
    const input = host.querySelector('input[type="checkbox"]');
    if (input instanceof HTMLInputElement) input.checked = host.dataset.rInCart === 'true';
  }
}

function markBusy() {
  for (const host of hosts()) {
    host.toggleAttribute('data-busy', pending > 0);
    host.setAttribute('aria-busy', pending > 0 ? 'true' : 'false');
  }
}

/**
 * Queues the intent to have (or not have) the gift box in the cart.
 * @param {boolean} want
 * @param {'toggle' | 'orphan' | 'excess'} reason - Only 'toggle' is the shopper's own action.
 * @returns {Promise<void>}
 */
function setGiftWrap(want, reason) {
  const ticket = ++latestTicket;
  pending += 1;
  markBusy();

  let settled = false;
  // Idempotent: called before a re-render (so the morph sees an idle element)
  // and again in `finally` for the paths that return early.
  const settle = () => {
    if (settled) return;
    settled = true;
    pending = Math.max(0, pending - 1);
    markBusy();
  };

  queue = queue
    .then(() => run({ ticket, want, reason, settle }))
    .catch((error) => console.error(`[${SOURCE}]`, error))
    .finally(settle);

  return queue;
}

/**
 * @param {object} job
 * @param {number} job.ticket
 * @param {boolean} job.want
 * @param {'toggle' | 'orphan' | 'excess'} job.reason
 * @param {() => void} job.settle
 */
async function run({ ticket, want, reason, settle }) {
  if (ticket !== latestTicket) return;

  const config = readConfig();
  if (!config) return;

  /** @type {Applied} */
  let applied;
  try {
    applied = await apply(want, config);
  } catch (error) {
    if (ticket !== latestTicket) return;
    console.warn(`[${SOURCE}] ${reason} failed`, error);
    settle();
    if (undispatched) {
      // An earlier write did land; show that cart rather than the one before it.
      const held = undispatched;
      undispatched = null;
      dispatch(held);
    } else {
      resync();
    }
    if (reason === 'toggle') showToast(config.errorMessage || String(error), { tone: 'error' });
    return;
  }

  if (reason === 'toggle') {
    track('cart_extra', {
      action: want ? 'gift_wrap_add' : 'gift_wrap_remove',
      cart_value: cartValue(applied.cart.items_subtotal_price),
    });
  }

  if (ticket !== latestTicket) {
    // A newer intent is queued and re-renders on its own; rendering this one
    // first would flip the checkbox back under the shopper's finger.
    undispatched = applied;
    return;
  }

  undispatched = null;
  settle();
  dispatch(applied);
}

/**
 * Removes a gift box with nothing left to wrap, and trims more than one to one.
 * @param {Cart | null | undefined} cart
 */
function reconcile(cart) {
  if (pending > 0 || !Array.isArray(cart?.items)) return;

  const config = readConfig();
  if (!config) return;

  const lines = giftLines(cart, config.productId);
  if (lines.length === 0) return;

  if (wrappableCount(cart, config) === 0) {
    void setGiftWrap(false, 'orphan');
  } else if (quantityOf(lines) > 1) {
    void setGiftWrap(true, 'excess');
  }
}

/** @param {Event} event */
function onCartUpdate(event) {
  // CartUpdateEvent extends Event and attaches `detail` by hand; a third party's
  // bare `new Event('cart:update')` has none.
  const detail = /** @type {any} */ (event).detail;
  if (detail?.data?.source === SOURCE) return;
  reconcile(detail?.resource);
}

/**
 * Holds a checkout submit until a gift box write settles. Navigating away aborts
 * an in-flight fetch, so a tick followed at once by Check out could otherwise
 * reach checkout without the box. Released on success and on failure alike —
 * the shopper is never stuck; a failure has already shown its toast.
 * @param {SubmitEvent} event
 */
function onSubmit(event) {
  if (pending === 0) return;
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;
  event.preventDefault();
  const submitter = event.submitter instanceof HTMLElement ? event.submitter : undefined;
  void queue.finally(() => form.requestSubmit(submitter));
}

/**
 * @typedef {object} Refs
 * @property {HTMLInputElement} checkbox
 * @extends {Component<Refs>}
 */
class RCartGiftWrap extends Component {
  requiredRefs = ['checkbox'];

  connectedCallback() {
    super.connectedCallback();
    markBusy();

    // A cart that arrived broken (another tab, an older product page) is fixed
    // from the server-rendered flags; there is no cart event to react to yet.
    if (pending === 0) {
      if (this.hasAttribute('data-r-orphan')) void setGiftWrap(false, 'orphan');
      else if (this.hasAttribute('data-r-excess')) void setGiftWrap(true, 'excess');
    }
  }

  /** @param {Event} event */
  onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    void setGiftWrap(input.checked, 'toggle');
  };
}

if (!customElements.get('r-cart-gift-wrap')) {
  customElements.define('r-cart-gift-wrap', RCartGiftWrap);
  document.addEventListener(ThemeEvents.cartUpdate, onCartUpdate);
  document.addEventListener('submit', onSubmit, true);
}
